import { DatePicker, Segmented, Select, Space } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { Period } from "../lib/period";

const { RangePicker } = DatePicker;

const currentYear = dayjs().year();
const LOOKBACK_YEARS = 2;

const serviceOptions = [
  { label: "AVCS", value: "AVCS", disabled: true },
  { label: "ADP", value: "ADP", disabled: true },
  { label: "eNP", value: "eNP", disabled: true },
];

type PeriodMode = "year" | "range";

interface FilterBarProps {
  period: Period;
  onPeriodChange: (p: Period) => void;
  services: string[];
  onServicesChange: (s: string[]) => void;
}

const inLookbackWindow = (date: Dayjs) =>
  date.year() >= currentYear - LOOKBACK_YEARS && date.year() <= currentYear;

export const FilterBar: React.FC<FilterBarProps> = ({
  period,
  onPeriodChange,
  services,
  onServicesChange,
}) => {
  // Drafts keep each mode's last value so toggling back restores it. A
  // half-picked range never reaches onPeriodChange: the page keeps showing
  // the previous selection until both ends are set.
  const [mode, setMode] = React.useState<PeriodMode>(period.mode);
  const [yearDraft, setYearDraft] = React.useState<number>(
    period.mode === "year" ? period.year : currentYear
  );
  const [rangeDraft, setRangeDraft] = React.useState<[Dayjs, Dayjs] | null>(
    period.mode === "range"
      ? [dayjs(`${period.fromMonth}-01`), dayjs(`${period.toMonth}-01`)]
      : null
  );

  const applyRange = (range: [Dayjs, Dayjs]) => {
    onPeriodChange({
      mode: "range",
      fromMonth: range[0].format("YYYY-MM"),
      toMonth: range[1].format("YYYY-MM"),
    });
  };

  const handleModeChange = (next: PeriodMode) => {
    setMode(next);
    if (next === "year") {
      onPeriodChange({ mode: "year", year: yearDraft });
    } else if (rangeDraft) {
      applyRange(rangeDraft);
    }
  };

  return (
    <Space>
      <Segmented<PeriodMode>
        options={[
          { label: "Year", value: "year" },
          { label: "Date Range", value: "range" },
        ]}
        value={mode}
        onChange={handleModeChange}
      />
      {mode === "year" ? (
        <DatePicker
          picker="year"
          allowClear={false}
          value={dayjs().year(yearDraft)}
          disabledDate={(date: Dayjs) => !inLookbackWindow(date)}
          onChange={(date) => {
            if (!date) return;
            setYearDraft(date.year());
            onPeriodChange({ mode: "year", year: date.year() });
          }}
        />
      ) : (
        <RangePicker
          picker="month"
          allowClear={false}
          value={rangeDraft}
          disabledDate={(date: Dayjs) => !inLookbackWindow(date)}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              const range: [Dayjs, Dayjs] = [dates[0], dates[1]];
              setRangeDraft(range);
              applyRange(range);
            }
          }}
        />
      )}
      <Select
        mode="multiple"
        style={{ minWidth: 240 }}
        value={services}
        onChange={(next) =>
          onServicesChange(next.includes("AVCS") ? next : ["AVCS", ...next])
        }
        options={serviceOptions}
      />
    </Space>
  );
};
